import React, { useCallback, useEffect, useState } from "react";
import {
  getPayrollAnalytics,
  getPayrollAnalyticsPortal,
} from "../../api/payroll/payrollAnalyticsApi";
import KPIGroup from "./KPIGroup";
import ChartCard from "./ChartCard";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import AreaChart from "./AreaChart";
import GroupedBarChart from "./GroupedBarChart";
import { useToast } from "../../context/ToastContext";

/* Payroll palette — distinct from fee (teal) and staff (indigo) */
const COLORS = {
  violet: "#7C3AED",
  fuchsia: "#C026D3",
  slate: "#475569",
  emerald: "#059669",
  amber: "#D97706",
  rose: "#E11D48",
};

const PAY_STATUS_COLORS = [COLORS.emerald, COLORS.amber, COLORS.rose];

const MONTHS = [
  { value: "", label: "All months" },
  { value: "1", label: "Jan" },
  { value: "2", label: "Feb" },
  { value: "3", label: "Mar" },
  { value: "4", label: "Apr" },
  { value: "5", label: "May" },
  { value: "6", label: "Jun" },
  { value: "7", label: "Jul" },
  { value: "8", label: "Aug" },
  { value: "9", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

const formatINR = (n) => {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const LoadingState = () => (
  <div className="flex h-40 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center">
    <p className="text-sm text-rose-700">{message || "Failed to load analytics"}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="rounded-md bg-rose-600 px-3 py-1 text-xs text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const PayrollAnalytics = ({ isPortal = false }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { year };
      if (month) params.month = month;

      const result = isPortal
        ? await getPayrollAnalyticsPortal(params)
        : await getPayrollAnalytics(params);
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [year, month, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const years = data?.filterOptions?.years?.length
    ? data.filterOptions.years
    : [currentYear, currentYear - 1];

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};

  const monthlyChart = (trends.monthly || []).map((m) => ({
    name: m.period,
    period: m.period,
    gross: m.gross || 0,
    net: m.net || 0,
    deductions: m.deductions || 0,
  }));

  const netTrend = monthlyChart.map((m) => ({
    period: m.period,
    value: m.net,
  }));

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-neutral-200">Payroll Overview</h2>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            {MONTHS.map((m) => (
              <option key={m.value || "all"} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={fetchData} />}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPIGroup
              title="Payout"
              items={[
                { label: "Staff", value: kpis.staffCount },
                {
                  label: "Gross",
                  value: formatINR(kpis.totalGross),
                },
                {
                  label: "Net",
                  value: formatINR(kpis.totalNet),
                  tone: "success",
                },
                {
                  label: "Deductions",
                  value: formatINR(kpis.totalDeductions),
                  tone: "warning",
                },
              ]}
            />
            <KPIGroup
              title="Payment status"
              items={[
                {
                  label: "Paid",
                  value: formatINR(kpis.paidAmount),
                  sub: `${kpis.paidCount || 0} slips`,
                  tone: "success",
                },
                {
                  label: "Pending",
                  value: formatINR(kpis.pendingAmount),
                  sub: `${kpis.pendingCount || 0} slips`,
                  tone: "warning",
                },
                {
                  label: "Avg net",
                  value: formatINR(kpis.avgNet),
                },
                {
                  label: "Overtime",
                  value: formatINR(kpis.totalOvertime),
                },
              ]}
            />
            <KPIGroup
              title="Statutory & LOP"
              items={[
                { label: "PF (emp)", value: formatINR(kpis.pfEmployee) },
                { label: "ESI (emp)", value: formatINR(kpis.esiEmployee) },
                { label: "PT + TDS", value: formatINR((kpis.professionalTax || 0) + (kpis.tds || 0)) },
                {
                  label: "LOP loss",
                  value: formatINR(kpis.totalLop),
                  tone: kpis.totalLop > 0 ? "danger" : "default",
                },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Gross vs Net vs Deductions (monthly)">
              <GroupedBarChart
                data={monthlyChart}
                series={[
                  { key: "gross", name: "Gross", color: COLORS.violet },
                  { key: "net", name: "Net", color: COLORS.emerald },
                  { key: "deductions", name: "Deductions", color: COLORS.amber },
                ]}
                height={220}
                layout="vertical"
              />
            </ChartCard>

            <ChartCard title="Payment status (amount)">
              <DonutChart
                data={distributions.byPaymentAmount || []}
                colors={PAY_STATUS_COLORS}
                height={220}
              />
            </ChartCard>
          </div>

          <ChartCard title="Net pay trend">
            <AreaChart
              data={netTrend}
              height={180}
              dataKey="value"
              xKey="period"
              color={COLORS.violet}
            />
          </ChartCard>

          <ChartCard title="Net cost by role">
            <BarChart
              data={distributions.byRole || []}
              layout="horizontal"
              height={200}
              color={COLORS.fuchsia}
              categoryWidth={110}
            />
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default PayrollAnalytics;
